import React, { useState } from "react";

import { useFetchBiInsightsQuery } from "../../../api/analyticsApi";
import { Icon, Pill } from "../../catalogProductStudio/edit/StudioUi";
import { DataState } from "../AnalyticsUi";
import { describeInsight, evidenceColumns, fill, formatDate, formatInsightValue } from "../analyticsModel";

const SEVERITY = {
  critical: { tone: "danger", icon: "alert" },
  warning: { tone: "warning", icon: "alert" },
  positive: { tone: "success", icon: "trendUp" },
  info: { tone: "info", icon: "info" },
};

const evidenceCell = (key, value, tt, lang) => {
  if (key === "status") return tt(`flag_${value}`);
  if (key === "period") return tt(value === "current" ? "currentPeriod" : "previousPeriod");
  if ((key === "category" || key === "title") && !value) return tt("uncategorised");
  if (key === "daysSinceLastSale" && (value === null || value === undefined)) return tt("never");
  return formatInsightValue(key, value, lang);
};

export const InsightCard = ({ insight, tt, lang, period, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const severity = SEVERITY[insight.severity] || SEVERITY.info;
  const { text, rule } = describeInsight(insight, tt, lang);
  const columns = evidenceColumns(insight.evidence);
  return (
    <article className={`an-insight is-${insight.severity}`}>
      <header className="an-insight__head">
        <Pill tone={severity.tone} icon={severity.icon}>
          {tt(`severity_${insight.severity}`)}
        </Pill>
        {period ? <span className="an-muted an-small">{period}</span> : null}
      </header>
      <p className="an-insight__text">{text}</p>
      <p className="an-insight__rule">
        <Icon name="sliders" size={13} /> {tt("rule")}: {rule}
      </p>
      {columns.length ? (
        <>
          <button type="button" className="pc-link" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
            {open ? tt("hideTable") : tt("evidence")}
          </button>
          {open ? (
            <div className="an-table-wrap">
              <table className="an-table is-dense">
                <thead>
                  <tr>
                    {columns.map((key) => (
                      <th key={key} scope="col">
                        {tt(`ev_${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {insight.evidence.map((row, index) => (
                    <tr key={index}>
                      {columns.map((key) => (
                        <td key={key}>{evidenceCell(key, row[key], tt, lang)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </article>
  );
};

const InsightsSection = ({ range, tt, lang }) => {
  const query = useFetchBiInsightsQuery({ dateFrom: range.dateFrom, dateTo: range.dateTo });
  const data = query.data;
  const period = data
    ? fill(tt("periodLabel"), {
        from: formatDate(data.range.dateFrom, lang),
        to: formatDate(data.range.dateTo, lang),
        days: data.range.days,
      })
    : null;

  return (
    <section className="an-section" aria-labelledby="an-insights-title">
      <div className="an-section__intro">
        <h3 id="an-insights-title" className="an-panel__title">
          {tt("insightsTitle")}
        </h3>
        <p className="an-panel__hint">{tt("insightsHint")}</p>
      </div>
      <DataState query={query} tt={tt} isEmpty={data && !data.insights.length} emptyTitle={tt("insightsEmpty")} emptyBody={period}>
        {data ? (
          <div className="an-insights">
            {data.insights.map((insight) => (
              <InsightCard key={insight.id} insight={insight} tt={tt} lang={lang} period={period} />
            ))}
          </div>
        ) : null}
      </DataState>
    </section>
  );
};

export default InsightsSection;
